import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sparkles, ArrowRight, Loader2, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function SetupWizard({ open, onDone, userName }) {
  const [step, setStep] = useState(0);
  const [dna, setDna] = useState({ company_profile: '', coaching_philosophy: '' });
  const [busy, setBusy] = useState(false);
  const [sampleLoaded, setSampleLoaded] = useState(false);

  const saveDna = async () => {
    setBusy(true);
    try { await api.put('/dna', dna); setStep(2); }
    catch { toast.error('Could not save'); } finally { setBusy(false); }
  };
  const loadSample = async () => {
    setBusy(true);
    try { await api.post('/sample-data'); setSampleLoaded(true); toast.success('Sample data loaded'); }
    catch { toast.error('Failed'); } finally { setBusy(false); }
  };
  const finish = async () => {
    setBusy(true);
    try { await api.put('/preferences', { onboarding_completed: true }); onDone(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="setup-wizard" onInteractOutside={(e) => e.preventDefault()}>
        <DialogTitle className="sr-only">Welcome Setup</DialogTitle>
        <DialogDescription className="sr-only">First-time setup wizard for Set The Pace OS.</DialogDescription>
        {step === 0 && (
          <div className="text-center py-3">
            <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center mx-auto mb-4"><Sparkles className="w-6 h-6 text-foreground" /></div>
            <h2 className="display text-3xl mb-2">Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}</h2>
            <p className="text-sm text-muted-foreground px-4">Set The Pace OS is your AI-powered business and coaching command centre. Let's get you set up in under a minute.</p>
            <button onClick={() => setStep(1)} data-testid="wizard-start" className="mt-6 bg-primary text-foreground text-sm px-5 py-2.5 rounded-sm hover:brightness-95 inline-flex items-center gap-2">Get started<ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
        {step === 1 && (
          <div className="py-2">
            <div className="micro-label mb-1">Step 1 of 3</div>
            <h2 className="display text-2xl mb-3">Tell the AI about your business</h2>
            <div className="space-y-3">
              <div><label className="micro-label">Your business in a few lines</label><textarea value={dna.company_profile} onChange={(e) => setDna({ ...dna, company_profile: e.target.value })} data-testid="wizard-company" rows={3} placeholder="What you do, who you help, your offers…" className="w-full mt-1 px-3 py-2 text-sm border border-zinc-200 rounded-sm resize-none" /></div>
              <div><label className="micro-label">Your coaching philosophy</label><textarea value={dna.coaching_philosophy} onChange={(e) => setDna({ ...dna, coaching_philosophy: e.target.value })} data-testid="wizard-philosophy" rows={3} placeholder="Your approach to coaching and behaviour change…" className="w-full mt-1 px-3 py-2 text-sm border border-zinc-200 rounded-sm resize-none" /></div>
            </div>
            <div className="flex justify-between mt-5">
              <button onClick={() => setStep(2)} className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:bg-zinc-50">Skip</button>
              <button onClick={saveDna} disabled={busy} data-testid="wizard-save-dna" className="bg-primary text-foreground text-sm px-4 py-2 rounded-sm hover:brightness-95 flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}Continue</button>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="py-2">
            <div className="micro-label mb-1">Step 2 of 3</div>
            <h2 className="display text-2xl mb-2">Explore with sample data</h2>
            <p className="text-sm text-muted-foreground mb-4">Load realistic demo contacts, tasks and coaching clients so you can explore every feature. You can clear it anytime from Settings.</p>
            <button onClick={loadSample} disabled={busy || sampleLoaded} data-testid="wizard-sample" className="w-full bg-primary text-foreground display text-sm tracking-wide px-4 py-2.5 rounded-sm hover:brightness-95 flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : sampleLoaded ? <Check className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}{sampleLoaded ? 'Sample data loaded' : 'Load sample data'}</button>
            <div className="flex justify-between mt-5">
              <button onClick={() => setStep(1)} className="text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:bg-zinc-50">Back</button>
              <button onClick={() => setStep(3)} data-testid="wizard-continue-step2" className="bg-primary text-foreground text-sm px-4 py-2 rounded-sm hover:brightness-95 flex items-center gap-2">Continue<ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="text-center py-3">
            <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center mx-auto mb-4"><Check className="w-6 h-6 text-green-700" /></div>
            <h2 className="display text-2xl mb-2">You're all set</h2>
            <p className="text-sm text-muted-foreground px-4">Open the Coaching Hub to see Today's Focus, or press ⌘/ to ask the AI assistant anything. You can take a full tour anytime from the Help Centre.</p>
            <button onClick={finish} disabled={busy} data-testid="wizard-finish" className="mt-6 bg-primary text-foreground display text-sm tracking-wide px-5 py-2.5 rounded-sm hover:brightness-95 inline-flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Start using the app</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
